import { EnumLanguage } from '@libs/constants';
import { Util } from '@libs/utilities/util';

import { EnumMbaAvailability, EnumMbaColor, EnumMbaFitType, EnumMbaMarketplace, EnumMbaPrint, EnumMbaProduct, EnumMbaPublish } from '../contracts/mba-amazon.constants';
import { IAmazonMbaMarketplace, IAmazonMbaPrint, IAmazonMbaProduct, MBA_PRODUCT_SPECIFICATION } from '../contracts/mba-products.constants';
import { IPodDesign } from '../contracts/pod-design.interface';
import { IMbaMarketplace, IMbaProduct, IMbaText, IPodListingMba } from '../contracts/pod-listing.interface';


/**------------------------------------------------------
 * Export Mba data in the old XML-Format
 */
export class MbaExportService {

	//** Exporting the mba data as xml */
	getXmlFormattedData(data: IPodDesign): IMbaXmlData {

		//0 - get the mba listing data
		const mbaData: IPodListingMba | null = data.listing?.mba;

		//1 - get helper variables / data
		const isAutoTranslate = Boolean(mbaData?.options.autoTranslate) && !Util.String.isEmpty(mbaData?.options.autoTranslate);
		const productXmlData : IMbaXmlProductData[] = this.getXmlProductsData(mbaData);

		//2 - get mba texts
		const mbaEnText = this.getText(mbaData, EnumLanguage.EN);
		const mbaDeText = this.getText(mbaData, EnumLanguage.DE);
		const mbaFrText = this.getText(mbaData, EnumLanguage.FR);
		const mbaItText = this.getText(mbaData, EnumLanguage.IT);
		const mbaEsText = this.getText(mbaData, EnumLanguage.ES);
		const mbaJaText = this.getText(mbaData, EnumLanguage.JA);

		//3 - format the mba data into the old xml standard
		const formattedData: IMbaXmlData = {
			mba_xml_version : '2.1',
			brand : {
				brand_en 		: this.getValidText(mbaEnText?.brand),
				brand_de 		: this.getValidText(mbaDeText?.brand),
				brand_fr 		: this.getValidText(mbaFrText?.brand),
				brand_it 		: this.getValidText(mbaItText?.brand),
				brand_es 		: this.getValidText(mbaEsText?.brand),
				brand_jp 		: this.getValidText(mbaJaText?.brand)
			},
			title : {
				title_en 		: this.getValidText(mbaEnText?.title),
				title_de 		: this.getValidText(mbaDeText?.title),
				title_fr 		: this.getValidText(mbaFrText?.title),
				title_it 		: this.getValidText(mbaItText?.title),
				title_es 		: this.getValidText(mbaEsText?.title),
				title_jp 		: this.getValidText(mbaJaText?.title)
			},
			bulletpoint1 : {
				bulletpoint1_en : this.getValidText(mbaEnText?.bullet1),
				bulletpoint1_de : this.getValidText(mbaDeText?.bullet1),
				bulletpoint1_fr : this.getValidText(mbaFrText?.bullet1),
				bulletpoint1_it : this.getValidText(mbaItText?.bullet1),
				bulletpoint1_es : this.getValidText(mbaEsText?.bullet1),
				bulletpoint1_jp : this.getValidText(mbaJaText?.bullet1)
			},
			bulletpoint2 : {
				bulletpoint2_en : this.getValidText(mbaEnText?.bullet2),
				bulletpoint2_de : this.getValidText(mbaDeText?.bullet2),
				bulletpoint2_fr : this.getValidText(mbaFrText?.bullet2),
				bulletpoint2_it : this.getValidText(mbaItText?.bullet2),
				bulletpoint2_es : this.getValidText(mbaEsText?.bullet2),
				bulletpoint2_jp : this.getValidText(mbaJaText?.bullet2)
			},
			description : {
				description_en 	: this.getValidText(mbaEnText?.description),
				description_de 	: this.getValidText(mbaDeText?.description),
				description_fr 	: this.getValidText(mbaFrText?.description),
				description_it 	: this.getValidText(mbaItText?.description),
				description_es 	: this.getValidText(mbaEsText?.description),
				description_jp 	: this.getValidText(mbaJaText?.description)
			},
			amazon_options : {
				availability_private : mbaData?.options.availability === EnumMbaAvailability.Private,
				save_draft 			 : mbaData?.options.publish 	 === EnumMbaPublish.Draft,
				auto_translate 		 : isAutoTranslate,
				auto_translate_main_language : isAutoTranslate ? (mbaData?.options.autoTranslate.toUpperCase() || 'EN') : 'EN'
			},
			products : {
				product : productXmlData			// products data
			}
		};

		//4 - return xml formatted data
		return formattedData;
	}


	/**------------------------------------------------------
	 * Products Helpers
	 */
	private getValidText(text: string | undefined): string {
		return text || '';
	}

	private getXmlProductsData(mbaData: IPodListingMba | null): IMbaXmlProductData[] {

		//0 - check if mby data exists
		if (!mbaData) return [];

		//1 - convert the products data
		const productsXmlData: IMbaXmlProductData[] = [];
		for (const product of mbaData.products) {
			const productData: IMbaXmlProductData = this.getXmlProductData(product);
			productsXmlData.push(productData);
		}

		return productsXmlData;
	}

	private getXmlProductData(product: IMbaProduct): IMbaXmlProductData {

		//0 - try to get the product specification
		const mbaProductSpecification = MBA_PRODUCT_SPECIFICATION.find((elem: IAmazonMbaProduct) => elem.code === product.productCode);
		if (!mbaProductSpecification) throw new Error(`MbaExportService => getXmlProductData => FATAL ERROR: no mba product specification found for the productCode of "${product.productCode}" (valid are: ${Util.Enum.values(EnumMbaProduct)})`);

		//1 - format the product data into the old xml standard
		const formattedData: IMbaXmlProductData = {
			name 		: mbaProductSpecification.oldCode,
			marketplace : {
				marketplace_com 	: this.getMarketplace(product, EnumMbaMarketplace.COM)   !== null,
				marketplace_co_uk 	: this.getMarketplace(product, EnumMbaMarketplace.CO_UK) !== null,
				marketplace_de		: this.getMarketplace(product, EnumMbaMarketplace.DE)	 !== null,
				marketplace_fr		: this.getMarketplace(product, EnumMbaMarketplace.FR)	 !== null,
				marketplace_it		: this.getMarketplace(product, EnumMbaMarketplace.IT)	 !== null,
				marketplace_es		: this.getMarketplace(product, EnumMbaMarketplace.ES)	 !== null,
				marketplace_jp		: this.getMarketplace(product, EnumMbaMarketplace.JP)	 !== null
			},
			price : {
				price_com 	: this.getPrice(product, EnumMbaMarketplace.COM, mbaProductSpecification),
				price_co_uk : this.getPrice(product, EnumMbaMarketplace.CO_UK, mbaProductSpecification),
				price_de 	: this.getPrice(product, EnumMbaMarketplace.DE, mbaProductSpecification),
				price_fr 	: this.getPrice(product, EnumMbaMarketplace.FR, mbaProductSpecification),
				price_it 	: this.getPrice(product, EnumMbaMarketplace.IT, mbaProductSpecification),
				price_es 	: this.getPrice(product, EnumMbaMarketplace.ES, mbaProductSpecification),
				price_jp 	: this.getPrice(product, EnumMbaMarketplace.JP, mbaProductSpecification)
			},
			fit_type : {
				man 	: product.fitTypes.includes(EnumMbaFitType.Men),
				woman 	: product.fitTypes.includes(EnumMbaFitType.Women),
				youth 	: product.fitTypes.includes(EnumMbaFitType.Youth),
				girls 	: product.fitTypes.includes(EnumMbaFitType.Girls),
				adultunisex : product.fitTypes.includes(EnumMbaFitType.AdultUnisex)
			},
			colors : {
				option : mbaProductSpecification.colors.background ? [] : product.colors
			},
			background : this.getBackgroundColor(product),
			print_area : {
				option 		: mbaProductSpecification.printAreas.map((printArea: IAmazonMbaPrint) => printArea.area),
				area_value	: this.getXmlPrintArea(product.print)
			}
		};

		return formattedData;
	}

	private getMarketplace(product: IMbaProduct, market: EnumMbaMarketplace): IMbaMarketplace | null {

		//0 - try to get the marketplace
		const marketplace = product.marketplaces.find((elem: IMbaMarketplace) => elem.market === market);
		if (!marketplace) return null;

		//1 - return the marketplace
		return marketplace;
	}

	private getPrice(product: IMbaProduct, market: EnumMbaMarketplace, productSpecification: IAmazonMbaProduct): number {

		//0 - get configured price
		const configuredPrice = this.getMarketplace(product, market)?.price;
		if (configuredPrice) return configuredPrice;

		//1 - get min price of the specifications
		const getMarketSpecification = productSpecification.marketplaces.find((elem: IAmazonMbaMarketplace) => elem.market === market);
		if (getMarketSpecification?.priceMin) return getMarketSpecification.priceMin;

		//2 - return the fallback value
		return -1;
	}

	private getBackgroundColor(product: IMbaProduct): string {

		//0 - is it a accessories product?
		const productConfig = MBA_PRODUCT_SPECIFICATION.find((amazonProduct: IAmazonMbaProduct) => amazonProduct.code === product.productCode);
		const hasBackground: boolean = productConfig?.colors.background ?? false;
		if (Util.Boolean.isFalse(hasBackground)) return '';

		//1 - return the background color
		const background = product.background || productConfig?.colors.defaultBackground;
		if (background === EnumMbaColor.NoColor) return EnumMbaColor.NoColor;
		if (!background || !Util.Color.isHexColor(background)) throw new Error(`MbaExportService => getBackgroundColor => FATAL ERROR: background color of "${background}" is not a valid hex color`);
		return background;
	}

	private getXmlPrintArea(printArea: EnumMbaPrint): EnumMbaPrint {
		switch (printArea) {
			case EnumMbaPrint.FrontPrint:
				return EnumMbaPrint.Front;

			case EnumMbaPrint.BackPrint:
				return EnumMbaPrint.Back;

			default:
				return printArea;
		}
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private getText(mbaData: IPodListingMba | null, lang: EnumLanguage): IMbaText | null {

		//0 - try to get the text
		const mbaText = mbaData?.texts.find((text: IMbaText) => text.language === lang);
		if (!mbaText) return null;

		//1 - return the found text
		return mbaText;
	}
}


//** Interfaces --------------------------------- */
export interface IMbaXmlData {
	mba_xml_version : '2.1';
	brand : {
		brand_en 		: string;
		brand_de 		: string;
		brand_fr 		: string;
		brand_it 		: string;
		brand_es 		: string;
		brand_jp 		: string;
	};
	title : {
		title_en 		: string;
		title_de 		: string;
		title_fr 		: string;
		title_it 		: string;
		title_es 		: string;
		title_jp 		: string;
	};
	bulletpoint1 : {
		bulletpoint1_en : string;
		bulletpoint1_de : string;
		bulletpoint1_fr : string;
		bulletpoint1_it : string;
		bulletpoint1_es : string;
		bulletpoint1_jp : string;
	};
	bulletpoint2 : {
		bulletpoint2_en : string;
		bulletpoint2_de : string;
		bulletpoint2_fr : string;
		bulletpoint2_it : string;
		bulletpoint2_es : string;
		bulletpoint2_jp : string;
	};
	description : {
		description_en 	: string;
		description_de 	: string;
		description_fr 	: string;
		description_it 	: string;
		description_es 	: string;
		description_jp 	: string;
	};
	amazon_options : {
		availability_private : boolean;
		save_draft 			 : boolean;
		auto_translate 		 : boolean;
		auto_translate_main_language : string;
	};
	products : {
		product : IMbaXmlProductData[];
	};
}

export interface IMbaXmlProductData {
	name 		: string;
	marketplace : {
		marketplace_com 	: boolean;
		marketplace_co_uk 	: boolean;
		marketplace_de		: boolean;
		marketplace_fr		: boolean;
		marketplace_it		: boolean;
		marketplace_es		: boolean;
		marketplace_jp		: boolean;
	};
	price : {
		price_com 	: number;
		price_co_uk : number;
		price_de 	: number;
		price_fr 	: number;
		price_it 	: number;
		price_es 	: number;
		price_jp 	: number;
	};
	fit_type : {
		man 	: boolean;
		woman 	: boolean;
		youth 	: boolean;
		girls 	: boolean;
		adultunisex : boolean;
	};
	colors : {
		option: string[];
	};
	background : string;
	print_area : {
		option 		: string[];
		area_value	: string;
	};
}
